import { DateTime } from "luxon";
import { DateTimeFormatOptions } from "vue-i18n";

export function formatLocaleDate(
  date: DateTime | null | undefined,
  locale?: string
): string {
  // ? if year is more than 4 digits, vuetify datepicker breaks, 
  // ? so i'm treating this as invalid value
  if (date && date.year < 10000) {
    const formated = locale
      ? date.setLocale(locale).toFormat("D")
      : date.toFormat("D");

    return formated;
  } else {
    return "";
  }
}

/**
 * Parse a localized date to luxon DateTime object.
 * @param {string?} stringDate - the localized string representing the date
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function parseLocaleDate(
  stringDate: string | null | undefined,
  locale?: string
): DateTime | null {
  if (!stringDate || stringDate === "") {
    return null;
  }

  const explained = DateTime.fromFormatExplain(
    formatLocaleDate(DateTime.local(), locale),
    "D",
    { locale }
  );

  // ? format keeps endianess(little, big, ?middle) and all special chars are replaced
  const normalizedFormat = explained.tokens
    .filter((x) => !x.literal)
    .map((x) => x.val)
    .join("-");

  const normalizedInput = stringDate
    .replace(/[\W_]+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

  const parsed = DateTime.fromFormat(normalizedInput, normalizedFormat, {
    locale
  });

  // ? if year is more than 4 digits, vuetify datepicker breaks, 
  // ? so i'm treating this as invalid value
  return parsed.isValid && parsed.year < 10000 ? parsed : null;
}

export function parseStringToDateJS(value: string) {
  const temp = new Date(value);
  return new Date(
    Date.UTC(temp.getFullYear(), temp.getMonth(), temp.getDate())
  );
}

export function differenceInDays(date1: Date, date2: Date) {
  const d1 = DateTime.fromJSDate(date1).set({ hour: 0 });
  const d2 = DateTime.fromJSDate(date2).set({ hour: 0 });
  const diff = d2.diff(d1, ["days"]).days;
  return diff;
}

export function differenceInYears(date1: Date, date2: Date) {
  const d1 = DateTime.fromJSDate(date1).set({ hour: 0 });
  const d2 = DateTime.fromJSDate(date2).set({ hour: 0 });
  const diff = d2.diff(d1, ["years"]).years;
  return diff;
}

export function getAge(dateOfBirth: string | Date) {
  const stringToday = new Date().toISOString().substring(0, 10);
  return Math.floor(
    differenceInYears(
      new Date(dateOfBirth as string),
      new Date(stringToday)
    )
  );
}

export function formatDateTimeAccordingToTimeZoneOrUTC(date: Date, locale: string, timeZone?: string) {
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
    timeZone: timeZone || (timeZone != "") ? timeZone : "Etc/UTC",
  } as DateTimeFormatOptions;
  
  const utcToLocal = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));

  return new Intl.DateTimeFormat(locale, options).format(utcToLocal);
}

export function getDateTimeOffsetString(date: Date): string{
  return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd'T'HH:mm:ssZZZ");
}